import React from "react";
import BottomSheet from "./BottomSheet";
import { ShopPlaylist } from "../types/User";
import { formatAmountForCopy } from "../utils/currency";
import "./FanShopSheet.css";
import { t } from "../i18n";

// Storefront v2 — what the fan sees (founder picks 5C → 5A).
//
// The DJ's page keeps ONE quiet banner (5C, so the page stays one screen and
// the song search stays instantly visible); tapping it opens this sheet with
// the cover-art shelf (5A).
//
// Tonight's set always sits first, and it wears the plain green gradient —
// NOT the sealed-record ribbon. That art is the "you bought it, it's on its
// way" moment and only appears after purchase (founder decision).
//
// A DJ with an empty shelf never gets here: RequestPage keeps today's
// straight-to-checkout banner for them, so nothing changes for most DJs.

export type ShopChoice = { kind: "live" } | { kind: "shop"; playlist: ShopPlaylist };

/** Free is a real price — never let a 0 fall through a falsiness check. */
export function shopPriceLabel(price: number | null | undefined, currency?: string | null): string {
  if (price === null || price === undefined) return "";
  if (price === 0) return t("fanShopSheet.free");
  return formatAmountForCopy(price, currency);
}

const LiveArt: React.FC = () => (
  <span className="fshop-live-art" aria-hidden>
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#24C961" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12h3l2-7 4 14 3-9 2 2h4" />
    </svg>
  </span>
);

interface Props {
  open: boolean;
  onClose: () => void;
  djName: string;
  currency?: string | null;
  /** Tonight's set — omitted when the DJ isn't selling it. */
  livePrice: number | null;
  playlists: ShopPlaylist[];
  onPick: (choice: ShopChoice) => void;
}

const FanShopSheet: React.FC<Props> = ({
  open, onClose, djName, currency, livePrice, playlists, onPick,
}) => (
  <BottomSheet open={open} title={`From ${djName}`} onClose={onClose}>
    <div className="fshop-list">
      {livePrice !== null && (
        <button type="button" className="fshop-card fshop-card--live" onClick={() => onPick({ kind: "live" })}>
          <LiveArt />
          <span className="fshop-mid">
            <span className="fshop-t">{t("fanShopSheet.tonightSSet")}</span>
            <span className="fshop-s">{t("fanShopSheet.deliveredAfterTheShow")}</span>
          </span>
          <span className="fshop-p">{shopPriceLabel(livePrice, currency)}</span>
        </button>
      )}

      {playlists.map((p) => (
        <button key={p.id} type="button" className="fshop-card" onClick={() => onPick({ kind: "shop", playlist: p })}>
          <span className="fshop-art" aria-hidden>
            {p.covers.length > 0 ? (
              p.covers.slice(0, 4).map((src, i) => <img key={i} src={src} alt="" />)
            ) : (
              <span className="fshop-art-blank" />
            )}
          </span>
          <span className="fshop-mid">
            <span className="fshop-t">{p.name}</span>
            <span className="fshop-s">
              {t("fanShopSheet.tracksInstant", { count: p.trackCount })}
            </span>
          </span>
          <span className="fshop-p">{shopPriceLabel(p.price, currency)}</span>
        </button>
      ))}
    </div>

    <div className="fshop-foot">{t("fanShopSheet.yoursOnSpotifyOr")}</div>
  </BottomSheet>
);

export default FanShopSheet;
